// Simple API client with proper CORS and error handling
class SimpleApiClient {
  private baseURL: string;
  private requestInterceptors: Array<(options: RequestInit) => RequestInit> =
    [];
  private responseInterceptors: Array<
    (response: Response) => Response | Promise<Response>
  > = [];

  constructor() {
    const base =
      (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';
    this.baseURL = base.replace(/\/$/, '') + '/api';

    // Add a default 401 handling interceptor
    this.addResponseInterceptor(async (response: Response) => {
      if (response.status === 401 && typeof window !== 'undefined') {
        // Avoid infinite redirect loops
        const currentPath = window.location.pathname + window.location.search;
        if (!currentPath.startsWith('/login')) {
          const loginUrl = `/login?next=${encodeURIComponent(currentPath)}`;
          // Use replace to avoid polluting history
          window.location.replace(loginUrl);
        }
      }
      return response;
    });
  }

  // Add request interceptor
  addRequestInterceptor(interceptor: (options: RequestInit) => RequestInit) {
    this.requestInterceptors.push(interceptor);
  }

  // Add response interceptor
  addResponseInterceptor(
    interceptor: (response: Response) => Response | Promise<Response>
  ) {
    this.responseInterceptors.push(interceptor);
  }

  private async makeRequest<T = any>(
    url: string,
    options: RequestInit
  ): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;

    let requestOptions: RequestInit = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      ...options,
    };

    // Don't set Content-Type for FormData - browser handles this
    if (requestOptions.body instanceof FormData) {
      const headers = requestOptions.headers as Record<string, string>;
      delete headers['Content-Type'];
    }

    // Add auth token if available
    const token = this.getAuthToken();
    if (token && requestOptions.headers) {
      (requestOptions.headers as Record<string, string>)['Authorization'] =
        `Bearer ${token}`;
    }

    // Apply request interceptors
    for (const interceptor of this.requestInterceptors) {
      requestOptions = interceptor(requestOptions);
    }

    try {
      let response = await fetch(fullUrl, requestOptions);

      // Apply response interceptors
      for (const interceptor of this.responseInterceptors) {
        response = await interceptor(response);
      }

      if (!response.ok) {
        // Enhanced error handling
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // Fallback to status text if no JSON error
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Check if response has content
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      }

      return response.text() as T;
    } catch (error) {
      console.error('API request failed:', {
        url: fullUrl,
        method: options.method || 'GET',
        error: error instanceof Error ? error.message : error,
      });
      throw error;
    }
  }

  private getAuthToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('auth_token');
    }
    return null;
  }

  async get<T = any>(url: string): Promise<T> {
    return this.makeRequest<T>(url, { method: 'GET' });
  }

  // Download binary/blob content while preserving auth/session
  async getBlob(url: string): Promise<Blob> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;
    const token = this.getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(fullUrl, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    return await response.blob();
  }

  async postBlob(url: string, data: unknown): Promise<Blob> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    return await response.blob();
  }

  async post<T = unknown>(url: string, data: unknown): Promise<T> {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return this.makeRequest<T>(url, {
      method: 'POST',
      body,
    });
  }

  async put<T = unknown>(url: string, data: unknown): Promise<T> {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return this.makeRequest<T>(url, {
      method: 'PUT',
      body,
    });
  }

  async patch<T = unknown>(url: string, data: unknown): Promise<T> {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return this.makeRequest<T>(url, {
      method: 'PATCH',
      body,
    });
  }

  async delete<T = any>(url: string): Promise<T> {
    return this.makeRequest<T>(url, { method: 'DELETE' });
  }
}

export const apiClient = new SimpleApiClient();
